<script lang="ts" setup>
  import {useI18n} from 'vue-i18n';
  import {computed, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpButtonIcon from '~/components/cp/CpButtonIcon/CpButtonIcon.vue';
  import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';

  const props = defineProps<{
    kind?: 'standard'| 'votedThumbUp' |'votedThumbDown' | 'alreadyThumbsUp' | 'alreadyThumbsDown' | 'disabled'
    likeValue: string
    dislikeValue: string
    userName?: string
    date?: string
  }>();

  const voted = ref(false);
  const votedValue = ref(false);

  const likeProps = computed(() => {
    return (voted.value && !votedValue.value) || props.kind === 'votedThumbDown' || props.kind === 'alreadyThumbsDown' || props.kind === 'disabled' ?
      'neutral' :
      props.kind === 'standard' && !voted.value ? 'primary' : 'secondary';
  });

  const dislikeProps = computed(() => {
    return (voted.value && votedValue.value) || props.kind === 'votedThumbUp' || props.kind === 'alreadyThumbsUp' || props.kind === 'disabled' ?
      'neutral' :
      props.kind === 'standard' && !voted.value ? 'primary' : 'secondary';
  });

  const likeNumber = ref(parseInt(props.likeValue));
  const dislikeNumber = ref(parseInt(props.dislikeValue));

  const emit = defineEmits({
    thumbUp: null,
    thumbDown: null,
  });

  function emitter(value: boolean) {
    if (props.kind === 'standard' && voted.value === false) {
      voted.value = true;
      if (value) {
        likeNumber.value++;
        votedValue.value = true;
        emit('thumbUp');
      } else {
        dislikeNumber.value++;
        votedValue.value = false;
        emit('thumbDown');
      }
    }
  }

  const {b, e} = useCpBem('cp-thumb-feedback');
  const {t} = useI18n();
</script>
<template>
    <div :class="b">
        <div :class="e`date-answer`">
            <CpIconStandard
                v-if="voted !== false"
                :icon="votedValue ? 'sentiment_very_satisfied' : 'sentiment_very_dissatisfied'"
                type="secondary"
            />
            <CpText variant="caption-bold" :type="kind==='votedThumbUp' || kind==='votedThumbDown' || voted ? 'secondary' : 'neutral-dark'">
                {{
                    kind==='votedThumbDown' || kind==='votedThumbUp' ? t('votedThumb') :
                    voted && votedValue ? t('thumbUp') :
                    voted && !votedValue ? t('thumbDown') : userName
                }}
            </CpText>
            <CpText
                v-if="kind!=='votedThumbUp' && kind!=='votedThumbDown' && voted===false"
                variant="caption-bold"
                type="neutral"
            >
                {{ date }}
            </CpText>
        </div>
        <div :class="e`thumb-answer`">
            <div :class="e`thumb`">
                <CpText
                    :type="dislikeProps"
                    variant="caption-bold"
                >
                    {{ dislikeNumber }}
                </CpText>
                <CpButtonIcon
                    data-testid="cp-thumb-feedback-button-thumb-down"
                    :type="dislikeProps"
                    kind="voted_off"
                    @click=" emitter(false)"
                />
            </div>
            <div :class="e`thumb`">
                <CpText
                    :type="likeProps"
                    variant="caption-bold"
                >
                    {{ likeNumber }}
                </CpText>
                <CpButtonIcon
                    data-testid="cp-thumb-feedback-button-thumb-up"
                    :type="likeProps"
                    kind="voted_on"
                    @click="emitter(true)"
                />
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
  .cp-thumb-feedback {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    &__date-answer {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 4px;
    }
    &__thumb-answer {
      display: flex;
      flex-direction: row;
      gap: 9px;
    }
    &__thumb {
      display: flex;
      gap: 4px;
      align-items: center;
    }
  }
</style>
<i18n locale="es-US" lang="yaml">
  votedThumb: '¡Ya has dejado tu voto aquí!'
  thumbUp: '¡Gracias, valoramos tu participación!'
  thumbDown: '¡Lamentamos que no te haya sido útil!'
</i18n>
